import type { ReactNode } from "react";

import toast from "react-hot-toast";
import type { Location } from "react-router";

import set from "lodash/set";
import {
  FileArchiveIcon,
  FileCodeIcon,
  FileIcon,
  FileImageIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoIcon,
  PresentationIcon,
} from "lucide-react";

import { getAssetUrl } from "simplydo/core/assets";
import {
  getUserFollowing as getUserFollowingHelper,
  userIsFollowingContext as userIsFollowingContextHelper,
  userIsFollowingContextObject as userIsFollowingContextObjectHelper,
} from "simplydo/core/following";
import { getBaseUrl } from "simplydo/core/helpers";
import { i18n } from "simplydo/core/i18n";
import {
  shouldShowIdeaEdiSection,
  shouldShowIdeaSection,
} from "simplydo/core/ideas";
import {
  type EditableIdea,
  type IdeaAuthorship,
  canEditIdea,
  hasRoleOnEntity,
  isIdeaAuthor,
} from "simplydo/core/permissions";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import type { PreparedUser, UserMe } from "simplydo/interfaces/types/userTypes";
import { IconData } from "simplydo/react/ui/icon";

import api from "api";
import { userCache } from "utils/currentUser";

import type { ChipUser } from "components/lib/Chips/types";
import ConfirmModal from "components/lib/Modal/ConfirmModal";
import CopyToClipboardModal from "components/lib/Modal/CopyToClipboardModal";
import { createConfirmation } from "components/lib/Modal/MountPoint";
import PromptModal from "components/lib/Modal/PromptModal";

import constants from "./constants";

type IdeaOwnerChallenge = NonNullable<
  OpenAPI.Schemas["ResponseGetIdea"]["ownerChallenge"]
>;

type IdeaWithOwnerChallenge = OpenAPI.Schemas["Idea"] & {
  ownerChallenge?: IdeaOwnerChallenge;
};

type IdeaWithOwnerIdeaTemplate = OpenAPI.Schemas["Idea"] &
  OpenAPI.Schemas["AssignIdeaOwnerIdeaTemplate"];

type IdeaWithTemplateAndChallenge = IdeaWithOwnerIdeaTemplate &
  IdeaWithOwnerChallenge;

type MessageThreadWithParticipants = OpenAPI.Schemas["MessageThread"] &
  OpenAPI.Schemas["AssignMessageThreadParticipantUsers"];

/** @deprecated Use UserMe — includes ownerOrganisation and availableOrganisations. */
type UserMeWithOrganisations = UserMe;

/** Challenge responses hydrate `projectBoardAutomaticAssignees`; these helpers don't read it. */
type ChallengeLike = Omit<
  OpenAPI.Schemas["Challenge"],
  "projectBoardAutomaticAssignees"
>;

type CachedIdea = OpenAPI.Schemas["ResponseGetIdea"];

// const store = getStore ? getStore() : null;

// Some global permissions are unscoped, meaning they are not tied to a specific organisation or challenge
// For example, viewing the superadmin dashboard supersedes the idea of an organisation, therefore the scope is irrelevant
// However, things like ghosting INTO an organisation are scoped, because you can only ghost into an organisation you have access to
// There are also some middle grounds, handled on a per-function basis. For example, listing recent users will only list users in organisations you have access to, but listing all users is a global permission
const UNSCOPED_GLOBAL_PERMISSIONS = [
  "super.viewDashboard",
  "super.listUsers",
  "super.createOrgs",
  "super.managePermissions",
  "super.listRecents",
  "super.listOrgs",
];

let isLocalStorageSupported: boolean | undefined = undefined;

const util = {
  isProduction() {
    return import.meta.env.VITE_IS_PRODUCTION === "true";
  },
  appName() {
    return "SimplyDo";
  },
  code() {
    const params = new SearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      return code;
    }
    const url = window.location.hostname.toLowerCase();
    if (url.indexOf("app.simplydo.co.uk") > -1) return null;
    if (url.indexOf("simplydo.co.uk") > -1) return url.split(".")[0];
    if (url.indexOf("localhost") > -1) return "simplydo";
    return null;
  },
  getBaseUrl(
    organisation:
      | Partial<
          Pick<
            OpenAPI.Schemas["Organisation"],
            "code" | "customDomain" | "customDomainEnabled"
          >
        >
      | undefined,
    path = "",
  ) {
    path = path ? `/${path.replace(/^\/+/, "")}` : ""; // Ensure path starts with single slash
    if (import.meta.env.VITE_STAGE === "dev") {
      return `${import.meta.env.VITE_WEB_URL ?? "http://localhost:4000"}${path}`;
    }
    return getBaseUrl(import.meta.env.VITE_STAGE, organisation, path);
  },
  hasPermission(
    user: Pick<OpenAPI.Schemas["User"], "permissions"> | null | undefined,
    permission: string,
    scope?: string | undefined,
    parentScope?: string | string[] | undefined,
  ) {
    if (!user || !user.permissions || !permission) return false;
    // See comments above for how unscope permissions work
    if (UNSCOPED_GLOBAL_PERMISSIONS.indexOf(permission) > -1) {
      for (const [scopeKey, permissions] of Object.entries(user.permissions)) {
        if (
          scopeKey.includes("global") &&
          scopeKey !== "global" &&
          permissions.indexOf(permission as any) > -1
        ) {
          return true;
        }
      }
    }
    if (user.permissions.global) {
      for (let i = 0; i < user.permissions.global.length; i += 1) {
        if (permission === user.permissions.global[i]) return true;
      }
    }
    if (scope) {
      const relevantRoles = [
        ...(user.permissions[scope] || []),
        ...(user.permissions[`global.${scope}`] || []),
      ];
      for (let i = 0; i < relevantRoles.length; i += 1) {
        if (permission === relevantRoles[i]) return true;
      }
      if (parentScope) {
        if (!Array.isArray(parentScope)) {
          parentScope = [parentScope];
        }
        const permParts = permission.split(".");
        const subScopePermission = `${permParts[0]}-${scope}.${permParts.slice(1).join(".")}`;
        for (const parentScopeItem of parentScope) {
          const subScopeRoles = [
            ...(user.permissions[parentScopeItem] || []),
            ...(user.permissions[`global.${parentScopeItem}`] || []),
          ];
          for (let i = 0; i < subScopeRoles.length; i += 1) {
            if (subScopePermission === subScopeRoles[i]) return true;
          }
        }
      }
    }
    return false;
  },
  hasAgreedTerms(user: UserMeWithOrganisations | undefined) {
    if (
      !user ||
      !user.legalAgreements ||
      !user.legalAgreements.privacyPolicy ||
      !user.legalAgreements.termsOfUse
    )
      return false;
    if (user?.ownerOrganisation?.customTerms?.enabled) {
      if (
        !(
          user.ownerOrganisation._id in
          (user.legalAgreements?.organisations ?? {})
        )
      )
        return false;

      const orgLegalAgreements =
        user.legalAgreements.organisations?.[user.organisation];
      if (!orgLegalAgreements) {
        return false;
      }
      const agreeCustom =
        orgLegalAgreements.filter(
          (a) => a.version === user.ownerOrganisation?.customTerms?.version,
        ).length > 0;
      if (!agreeCustom) {
        return false;
      }
    }
    const agreePrivacy =
      user.legalAgreements.privacyPolicy.filter(
        (a) => a.version === constants.termsVersion,
      ).length > 0;
    const agreeTerms =
      user.legalAgreements.termsOfUse.filter(
        (a) => a.version === constants.termsVersion,
      ).length > 0;
    return !!(agreePrivacy && agreeTerms);
  },
  hasVerifiedEmail(
    user: Pick<OpenAPI.Schemas["User"], "emails"> | null | undefined,
    organisation:
      | Pick<OpenAPI.Schemas["Organisation"], "emailValidationEnforcing">
      | undefined,
  ) {
    if (!user) return false;
    if (!organisation || !organisation.emailValidationEnforcing) return true;
    if (!user.emails || !user.emails.length) return false;
    return user.emails[0]?.verified;
  },
  hasSetUpMFA(
    user: UserMe | null | undefined,
    organisation:
      | Pick<OpenAPI.Schemas["Organisation"], "mfaEnforcing">
      | undefined,
  ) {
    if (!user) return false;
    if (!organisation?.mfaEnforcing) return true;
    if (user.ssoUser) return true;
    if (user.mfaSatisfied !== undefined) return user.mfaSatisfied;
    return (user.mfaMethods?.length ?? 0) > 0;
  },
  canCreateChallenges(user: UserMeWithOrganisations | null | undefined) {
    const org = user?.ownerOrganisation;
    if (
      org?.enabledFeatures &&
      org.enabledFeatures.indexOf("challenges") > -1
    ) {
      if (
        util.hasPermission(user, "org.createChallenges", user?.organisation)
      ) {
        return true;
      }
    }
    return false;
  },
  canManageChallenge(
    user: Pick<OpenAPI.Schemas["User"], "permissions"> | null | undefined,
    challenge: Pick<OpenAPI.Schemas["Challenge"], "organisation"> | undefined,
  ) {
    return (
      !!user &&
      !!challenge &&
      util.hasPermission(user, "org.manageChallenges", challenge.organisation)
    );
  },
  canActionIdeas(
    user: UserMeWithOrganisations | null | undefined,
    challenge: ChallengeLike | undefined,
  ) {
    if (!challenge || !user) {
      return false;
    }
    return (
      util.hasPermission(user, "challenge.moveProjectIdeas", challenge?._id) ||
      util.hasPermission(
        user,
        "challenge.editProjectAssignees",
        challenge?._id,
      ) ||
      util.hasPermission(user, "challenge.editProjectDates", challenge?._id) ||
      util.hasPermission(
        user,
        "challenge.editProjectAssignees",
        challenge?._id,
      ) ||
      util.hasPermission(user, "challenge.editProjectDates", challenge?._id) ||
      util.hasPermission(user, "challenge.pinIdeas", challenge?._id) ||
      util.hasPermission(user, "challenge.editIdeaDeadlines", challenge?._id) ||
      util.hasPermission(user, "challenge.editIdeaLabels", challenge?._id) ||
      util.hasPermission(user, "challenge.mergeIdeas", challenge?._id) ||
      util.hasPermission(user, "challenge.editAssessments", challenge?._id) ||
      util.hasPermission(
        user,
        "challenge.moveIdeasToChallenge",
        challenge?._id,
      ) ||
      util.hasPermission(user, "org.addToGroupsDirectly", user?.organisation) ||
      util.hasPermission(user, "org.managePermissions", user?.organisation) ||
      util.hasPermission(user, "challenge.deleteIdeas", challenge?._id)
    );
  },
  canCreateIdea(
    user: UserMeWithOrganisations | null | undefined,
    challenge: ChallengeLike | undefined,
  ) {
    // only users in the challenges audience can create an idea
    if (user && challenge) {
      const ownerOrgId = user.ownerOrganisation?._id;
      return (
        (!!ownerOrgId &&
          challenge.visibility?.organisations?.includes(ownerOrgId)) ||
        user.ownerOrganisations?.some((org) =>
          challenge.visibility?.organisations?.includes(org._id),
        ) ||
        user.groups?.some((group) =>
          challenge.visibility?.groups?.includes(group._id!),
        )
      );
    }
    return false;
  },
  // Audience, managers, and public links. Assessors are excluded: being assigned
  // to assess does not grant the idea list, questions, or project board.
  canViewChallengeAsAudience(
    user: UserMeWithOrganisations | null | undefined,
    challenge: ChallengeLike | undefined,
  ) {
    if (!challenge) return false;
    if (util.canManageChallenge(user, challenge)) return true;
    if (challenge.visibility?.publicLink && challenge.stage) return true;
    if (util.hasPermission(user, "challenge.viewDetails", challenge._id)) {
      return true;
    }
    return !!challenge.stage && util.canCreateIdea(user, challenge);
  },
  canViewProjectBoard(
    user: UserMeWithOrganisations | null | undefined,
    challenge: ChallengeLike | undefined,
  ) {
    if (!user || !challenge) return false;
    if (util.canManageChallenge(user, challenge)) return true;
    if (util.hasPermission(user, "challenge.viewProjectBoard", challenge._id)) {
      return true;
    }
    return (
      challenge.projectManagementVisibility === "users" &&
      util.canViewChallengeAsAudience(user, challenge)
    );
  },
  isInGroup(
    user: PreparedUser | UserMe | null | undefined,
    group: OpenAPI.Schemas["Group"] | undefined,
  ) {
    return !!(group && user && hasRoleOnEntity(user, group._id));
  },
  canEditIdea(
    user: OpenAPI.Schemas["User"] | UserMe | null | undefined,
    idea: EditableIdea | undefined,
  ) {
    return canEditIdea(user, idea);
  },
  isIdeaAuthor(
    user:
      | Pick<OpenAPI.Schemas["User"], "_id" | "permissions">
      | null
      | undefined,
    idea: IdeaAuthorship | undefined,
  ) {
    return isIdeaAuthor(user, idea);
  },
  canJoinIdeaChat(
    user: OpenAPI.Schemas["User"] | UserMe | null | undefined,
    idea:
      | (IdeaWithOwnerChallenge & OpenAPI.Schemas["AssignIdeaAuthors"])
      | undefined,
  ) {
    if (!user || !idea) return false;
    const canEdit = this.canEditIdea(user, idea);
    const { collaborators = [] } = idea;
    return canEdit && collaborators.length;
  },
  avatarUrl(
    user?:
      | PreparedUser
      | UserMe
      | ChipUser
      | { profile?: { avatar?: string; fullName?: string } }
      | undefined,
    size = "100x100",
  ) {
    const avatar = user?.profile?.avatar;
    if (avatar?.match(/^[0-9]+\.png/)) {
      return getAssetUrl(`images/avatars/${avatar}`);
    }
    return util.getThumbnail(
      user?.profile,
      "avatar",
      size,
      "images/avatars/default.png",
    ) as string;
  },
  ideaCoverImage(
    idea:
      | {
          coverImage?: string | null;
          coverImageUrl?: string | null;
          coverImageThumbnailUrls?: Record<string, string> | null;
        }
      | undefined,
    size = "2560x400",
  ) {
    return util.getThumbnail(idea, "coverImage", size, "images/idea.png");
  },
  challengeImage(
    challenge?:
      | {
          coverImage?: string | null;
          bannerImage?: string | null;
          coverImageUrl?: string | null;
          bannerImageUrl?: string | null;
          coverImageThumbnailUrls?: Record<string, string> | null;
          bannerImageThumbnailUrls?: Record<string, string> | null;
        }
      | undefined,
  ) {
    return util.getImageUrl(
      challenge,
      ["coverImage", "bannerImage"],
      ["1280x200", "300x300"],
      "images/challengeBanner.jpg",
    );
  },
  groupCoverImage(
    group: Pick<OpenAPI.Schemas["Group"], "coverImage"> | undefined,
    size = "200x200",
  ) {
    return util.getThumbnail(
      group,
      "coverImage",
      size,
      "images/groupCoverImage.png",
    );
  },
  groupBannerImage(
    group: Pick<OpenAPI.Schemas["Group"], "bannerImage"> | undefined,
  ) {
    return util.getThumbnail(
      group,
      "bannerImage",
      ["2560x400", "1280x200"],
      "images/groupCoverImage.png",
    );
  },
  mixinCssUrlFallback(origUrl: string, ...fallbackUrls: string[]) {
    let mixinUrl = `${origUrl}), linear-gradient(white, white`;
    fallbackUrls.forEach((url) => {
      mixinUrl = `${mixinUrl}), url(${url}`;
    });
    return mixinUrl;
  },
  challengeLink(challenge: Pick<ChallengeLike, "_id"> | undefined) {
    if (!challenge) return "";
    const link = `/challenges/${challenge._id}`;
    return link;
  },
  getFileIcon(file: string): IconData {
    let ext = file.split(".").pop();
    if (ext) {
      ext = ext.toLowerCase();
      if (ext === "pdf") return FileTextIcon;
      if (ext === "csv" || ext === "xls" || ext === "xlsx")
        return FileSpreadsheetIcon;
      if (ext === "doc" || ext === "docx") return FileTextIcon;
      if (ext === "jpg" || ext === "png" || ext === "jpeg" || ext === "gif")
        return FileImageIcon;
      if (ext === "ppt" || ext === "pptx") return PresentationIcon;
      if (ext === "zip" || ext === "tar" || ext === "gx")
        return FileArchiveIcon;
      if (ext === "mp4" || ext === "avi" || ext === "mov") return FileVideoIcon;
      if (
        ext === "py" ||
        ext === "js" ||
        ext === "md" ||
        ext === "java" ||
        ext === "html" ||
        ext === "css"
      )
        return FileCodeIcon;
    }
    return FileIcon;
  },
  pluralise(count: number, s1: string, s2: string, includeNumber = true) {
    return `${includeNumber ? `${count || 0} ` : ""}${count === 1 ? s1 : s2}`;
  },
  ensureHttp(s: string | undefined) {
    if (!s) return;
    if (s.indexOf("http") === -1) return `http://${s}`;
    return s;
  },
  activePath(pattern: string | RegExp, location: Location | undefined) {
    const match = (location || window.location).pathname.match(
      new RegExp(pattern, "i"),
    );
    return (match && match.length > 0) || false;
  },
  confirm(title: string, content: ReactNode) {
    const confirm = createConfirmation(ConfirmModal);
    return confirm({ title, confirmation: content });
  },
  prompt(title: string, question: string, onComplete: (value: string) => void) {
    const prompt = createConfirmation(PromptModal);
    return prompt({ title, prompt: question, onComplete } as Parameters<
      typeof prompt
    >[0]);
  },
  async copyToClipboard(text: string) {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(text);
      toast.success(i18n.t("generic.copiedToClipboard"));
    } catch {
      const showCopyModal = createConfirmation(CopyToClipboardModal);
      await showCopyModal({ text });
    }
  },
  loginWithSaml(code: string) {
    const params = new SearchParams(window.location.search);

    // Check if we are already handling a redirection
    let then: string;
    const thenParam = params.get("then");
    if (thenParam) {
      params.delete("then");
      then = thenParam;
    } else {
      then = window.location.pathname;
    }

    const finalParams = new SearchParams();
    // Append remaining search params (excluding "then", which was popped earlier)
    then += params.toSafeString();
    finalParams.set("next_path", then);

    let url;
    if (util.isProduction()) {
      url = `https://${code}.simplydo.co.uk${import.meta.env.VITE_SAML_ENDPOINT}`;
    } else {
      finalParams.set("code", code);
      url = import.meta.env.VITE_SAML_ENDPOINT;
    }

    window.location.href = url + finalParams.toSafeString();
  },
  logoutWithSaml(nameId: string | undefined, code: string) {
    if (nameId) {
      if (util.isProduction()) {
        window.location.href = `https://${code}.simplydo.co.uk${import.meta.env.VITE_SAML_LOGOUT}?name_id=${nameId}`;
      } else {
        window.location.href = `${import.meta.env.VITE_SAML_LOGOUT}?name_id=${nameId}`;
      }
    }
  },
  loginWithOAuth(service: string, code: string) {
    const params = new SearchParams(window.location.search);

    // Check if we are already handling a redirection
    let then: string;
    const thenParam = params.get("then");
    if (thenParam) {
      params.delete("then");
      then = thenParam;
    } else {
      then = window.location.pathname;
    }

    // Append remaining search params (excluding "then", which was popped earlier)
    then += params.toSafeString();

    window.location.href = `${import.meta.env.VITE_LOGIN_SERVICE}/auth/oauth/${service}/login?code=${code}${then && then !== "/" ? `&next_path=${encodeURIComponent(then)}` : ""}`;
  },
  connectOAuth(
    service: string,
    user: UserMeWithOrganisations,
    type: string,
    typeId: string,
    contextId: string,
    forRole: string | undefined,
  ) {
    const params = new SearchParams({
      user: user._id,
      [type]: typeId,
      contextId,
      forRole,
      returnTo: encodeURIComponent(window.location.href),
    });

    if (user?.ownerOrganisation) {
      params.set("code", user.ownerOrganisation.code);
    }

    window.location.href = `${import.meta.env.VITE_LOGIN_SERVICE}/auth/oauth/${service}/connect${params.toSafeString()}`;
  },
  adjustColour(input: string, lum: number) {
    let hex = String(input).replace(/[^0-9a-f]/gi, "");
    if (!hex) return input;
    if (hex.length !== 6 && hex.length !== 3) return input;

    if (hex.length < 6) {
      hex = hex[0]! + hex[0]! + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const adjustment = lum || 0;

    let rgb = "#";
    let c;
    for (let i = 0; i < 3; i += 1) {
      if (typeof hex === "string") {
        c = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
        c = Math.round(Math.min(Math.max(0, c + c * adjustment), 255)).toString(
          16,
        );
        rgb += `00${c}`.substring(c.length);
      }
    }
    return rgb;
  },
  hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;
    if (result.length !== 4) return null;
    return result
      ? {
          r: parseInt(result[1]!, 16),
          g: parseInt(result[2]!, 16),
          b: parseInt(result[3]!, 16),
        }
      : null;
  },
  hexToRgbString(hex: string, opacity: number) {
    const rgb = util.hexToRgb(hex);
    if (!rgb) return hex;
    return opacity
      ? `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`
      : `rgb(${rgb.r},${rgb.g},${rgb.b})`;
  },
  handleNewIdea(
    challenge: ChallengeLike | undefined,
    user: Pick<OpenAPI.Schemas["User"], "permissions"> | null | undefined,
    navigateFunction: (
      idea: OpenAPI.POST<"/challenges/{id}/ideas">["response"],
    ) => void,
  ) {
    if (!challenge) return;

    const { _id, name } = challenge;
    api.challenges.createIdea(
      _id,
      `${i18n.t("common:capitalise", { key: "generic.idea" })} for ${name || "Undefined"}`,
      (idea) => {
        userCache.addIdeaId(idea._id);
        api.invalidateQueries(api.challenges.get, [challenge._id]);
        api.invalidateQueries(api.challenges.getIdeas, [challenge._id]);
        navigateFunction(idea);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  },
  newIdea(
    challenge: ChallengeLike | undefined,
    user: Pick<OpenAPI.Schemas["User"], "permissions"> | null | undefined,
    navigateFunction: (
      idea: OpenAPI.POST<"/challenges/{id}/ideas">["response"],
    ) => void,
  ) {
    if (
      challenge?.stage !== "published" &&
      this.canManageChallenge(user, challenge)
    ) {
      this.confirm(
        `Closed ${i18n.t("generic.challenge")}`,
        `You're attempting to make ${i18n.t("generic.ideaWithArticle")} for a closed ${i18n.t("generic.challenge")}. Only ${i18n.t("generic.challenge")} admins can do this; You will need to open your ${i18n.t("generic.challenge")} before other users can make ${i18n.t("generic.ideas")}`,
      )
        .then(() => this.handleNewIdea(challenge, user, navigateFunction))
        .catch(() => {});
    } else this.handleNewIdea(challenge, user, navigateFunction);
  },
  getProfileName(
    owner: { name?: string; profile?: { fullName?: string } } | undefined,
  ) {
    return owner?.name || owner?.profile?.fullName || "Someone";
  },
  getName(user: any | undefined) {
    if (!user) {
      return "Someone";
    }
    // These should cover the case for anything that is not a user
    if (user.name) {
      return user.name;
    }
    if (user.title) {
      return user.name;
    }
    if (user.subject) {
      return user.subject;
    }
    if (!user.profile) {
      return "Someone";
    }
    if (!user.profile.fullName) {
      return "Someone";
    }
    return user.profile.fullName;
  },
  getEventNames(events: any[], ownerPath = "ownerUser") {
    const firstEventOwner = events[0]?.[ownerPath];
    const secondEventOwner =
      events
        .map((e) => e[ownerPath] || {})
        .find(
          (ownerUser) =>
            ownerUser._id && ownerUser._id !== firstEventOwner?._id,
        ) || {};
    const uniqueEntityIds = [];
    for (let i = 0; i < events.length; i += 1) {
      const event = events[i];
      const eventOwnerEntity = event[ownerPath];
      if (
        eventOwnerEntity &&
        uniqueEntityIds.indexOf(eventOwnerEntity._id) === -1 &&
        eventOwnerEntity._id !== secondEventOwner._id &&
        eventOwnerEntity._id !== firstEventOwner._id
      ) {
        uniqueEntityIds.push(eventOwnerEntity._id);
      }
    }
    let initialString = util.getName(firstEventOwner);
    if (!util.isEmpty(secondEventOwner)) {
      if (uniqueEntityIds.length > 0) initialString += ", ";
      else initialString += " and ";
      initialString += util.getName(secondEventOwner);
    }
    if (uniqueEntityIds.length > 0) {
      initialString += ` and ${util.pluralise(uniqueEntityIds.length, "other", "others")}`;
    }
    return initialString;
  },
  getCalendarEventNames(events: any[]) {
    const titles = events.map((event) => event?.event?.title || "[Deleted]");
    if (titles.length <= 1) {
      return titles[0] || "";
    }
    if (titles.length === 2) {
      return `${titles[0]} and ${titles[1]}`;
    }
    const othersCount = titles.length - 2;
    return `${titles[0]}, ${titles[1]} and ${util.pluralise(othersCount, "other", "others")}`;
  },
  isEmpty(obj: any) {
    if (!obj) {
      return true;
    }
    if (Array.isArray(obj)) {
      return obj.length === 0;
    }
    if (typeof obj === "string") {
      return obj === "";
    }
    return Object.keys(obj).length === 0;
  },
  deepishClone(data: any) {
    if (data && typeof data === "object") {
      if (Array.isArray(data)) {
        data = [...data];
        data = data.map((d: any) => {
          if (typeof d === "object" && !d.length) {
            return { ...d };
          }
          return d;
        });
      } else {
        data = { ...data };
      }
    }
    return data;
  },
  shouldShowInterestsChooser(user: UserMeWithOrganisations | null | undefined) {
    if (!user) return false;
    if (!user.ownerOrganisation) return false;

    const options = user.ownerOrganisation.tagList?.map((t) => t._id);
    if (options?.length === 0 || !options) {
      return false;
    }
    const canChoose = user.ownerOrganisation?.onboarding?.enforceTagSelection;
    const chosen = user.tags || [];

    return (
      canChoose && chosen.filter((tag) => options.includes(tag)).length === 0
    );
  },
  getChatName(
    user: OpenAPI.Schemas["User"] | UserMe | null | undefined,
    thread: MessageThreadWithParticipants,
  ) {
    if (!user || !thread) return "Unknown user";

    const { name, participantUsers = [] } = thread;
    if (name) return name;
    const filteredUsers = participantUsers.filter((p) => p._id !== user._id);
    if (filteredUsers.length === 0) return "Unknown user";
    if (filteredUsers.length === 1) return filteredUsers[0]!.profile.fullName;
    let basicName = `You, ${filteredUsers
      .slice(0, 2)
      .map((u) => u.profile.fullName)
      .join(", ")}`;
    if (filteredUsers.length > 2) {
      const otherUsers = filteredUsers.length - 2;
      basicName += ` + ${otherUsers} others`;
    }
    return basicName;
  },
  getChatAvatar(
    user: OpenAPI.Schemas["User"] | UserMe,
    thread: MessageThreadWithParticipants,
  ) {
    const { participantUsers = [] } = thread;
    const filteredUsers = participantUsers?.filter((p) => p._id !== user._id);
    if (filteredUsers.length === 1) return util.avatarUrl(filteredUsers[0]);
    return null;
  },
  // https://stackoverflow.com/questions/21741841/detecting-ios-android-operating-system
  getMobileOperatingSystem() {
    // @ts-ignore - navigator is not typed
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return "Windows Phone";
    }
    if (/android/i.test(userAgent)) {
      return "Android";
    }
    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    // @ts-ignore - window is not typed
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "iOS";
    }
    return "unknown";
  },
  shouldShowIdeaSection(
    idea: IdeaWithOwnerIdeaTemplate | undefined,
    section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
  ) {
    return shouldShowIdeaSection(idea, section);
  },
  shouldShowIdeaEdiSection(
    user: OpenAPI.Schemas["User"] | UserMe | null | undefined,
    idea: IdeaWithTemplateAndChallenge | undefined,
    section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
  ) {
    return shouldShowIdeaEdiSection(user ?? undefined, idea, section);
  },
  browserIsSupported() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIE = userAgent.indexOf("trident") > -1;
    const isNonChromiumEdge = userAgent.indexOf("edge") > -1;
    return !isIE && !isNonChromiumEdge;
  },
  validateEmail(email: string) {
    // This is not all encompassing
    // Do not expect this to completely validate every possible email address
    // It's just for simple front end validation, so people dont enter their name in the email field
    return /^\S+@\S+$/.test(email);
  },
  validateUrl(value: string) {
    // https://stackoverflow.com/a/15855457
    return /(^|\s)((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)/i.test(
      value,
    );
  },
  getUrlPath(url: string) {
    if (!this.validateUrl(url)) return undefined;
    const hostMatch = url.match(
      /(^|\s)((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)/,
    );
    if (!hostMatch) {
      return "";
    }

    return hostMatch[6] || "";
  },
  /*
    An object in the following away will look like the following
    {
      type: "challenge",
      _id: ObjectId(),
      createdAt: Date(),
      following: true,
      unfollowed: false
    }
    The `unfollowed` key will be set to true (and following set to false) if the user manually unfollows the item. We use this to check if
    we should automatically re-follow the item once they interact with it again. For example, if a user unfollows a challenge, commenting
    on the discussion won't refollow
  */
  getUserFollowing(
    user: Pick<OpenAPI.Schemas["User"], "following"> | null | undefined,
    context: string,
  ) {
    return getUserFollowingHelper(user, context);
  },
  userIsFollowingContext(
    user: Pick<OpenAPI.Schemas["User"], "following"> | null | undefined,
    context: string,
    id: string,
  ) {
    return userIsFollowingContextHelper(user, context, id);
  },
  userIsFollowingContextObject(
    user: Pick<OpenAPI.Schemas["User"], "following"> | null | undefined,
    context: string,
    id: string,
  ) {
    return userIsFollowingContextObjectHelper(user, context, id);
  },
  parseJwt(token: string) {
    if (!token) return;
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return;
    }
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  },
  moveArrayItem(arr: any[], from: number, to: number) {
    const newArr = [...arr];
    const item = newArr.splice(from, 1)[0];
    newArr.splice(to, 0, item);
    return newArr;
  },
  limitNumberInput(
    e: React.ChangeEvent<HTMLInputElement>,
    success: (value: number | string) => void,
    fail: (value: number | string) => void = () => {},
  ) {
    if (!e.target.value) success("");
    const numericalInput = parseInt(e.target.value, 10);
    if (!Number.isNaN(numericalInput)) {
      success(numericalInput);
    } else fail(e.target.value);
  },
  organisationFeaturesEnabled(
    user: UserMeWithOrganisations | null | undefined,
    requiredFeatures: OpenAPI.Schemas["OrganisationFeature"][],
  ) {
    const enabledFeatures = user?.ownerOrganisation?.enabledFeatures || [];
    return requiredFeatures.every(
      (feature) => enabledFeatures.indexOf(feature) > -1,
    );
  },
  localStorageIsSupported() {
    if (isLocalStorageSupported !== undefined) {
      return isLocalStorageSupported;
    }
    try {
      const key = "test_key_simplydo_local_storage";
      localStorage.setItem(key, key);
      localStorage.removeItem(key);
      return true;
    } catch (_e) {
      return false;
    }
  },
  getImageUrl(
    entity: any,
    fields: string | string[],
    sizes: string | string[],
    defaultUrl?: string,
  ): string {
    if (sizes && !Array.isArray(sizes)) {
      sizes = [sizes];
    }
    if (fields && !Array.isArray(fields)) {
      fields = [fields];
    }
    if (entity && fields) {
      for (const field of fields) {
        if (sizes) {
          if (entity[field]) {
            if (entity[field].startsWith("unsplash-")) {
              const [w, h] = sizes[0]!.split("x");
              return `${entity[field].slice(9)}&fit=min&w=${w}&h=${h}`;
            }
          }
          const thumbnailField = `${field}ThumbnailUrls`;
          if (entity[thumbnailField]) {
            for (const size of sizes) {
              if (entity[thumbnailField][size]) {
                return entity[thumbnailField][size];
              }
            }
          }
        }
        const regularField = `${field}Url`;
        if (entity[regularField]) {
          return entity[regularField];
        }
      }
    }
    return defaultUrl ? getAssetUrl(defaultUrl) : "";
  },
  getThumbnail(
    entity: any,
    field: string,
    size: string | string[],
    defaultUrl?: string,
  ) {
    return util.getImageUrl(entity, field, size, defaultUrl);
  },
  convertToDayOrWeek(hours: number) {
    if (!hours) {
      return null;
    }
    const isNegative = hours < 0 ? "-" : "";
    hours = Math.abs(hours);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      if (days >= 7) {
        const weeks = Math.floor(days / 7);
        let result = `${isNegative}${weeks} week${weeks === 1 ? "" : "s"}`;

        const remainingDays = days % 7;
        if (remainingDays > 0) {
          result += `, ${remainingDays} day${remainingDays === 1 ? "" : "s"}`;
        }

        return result;
      }

      let result = `${isNegative}${days} day${days === 1 ? "" : "s"}`;
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        result += `, ${remainingHours} hour${remainingHours === 1 ? "" : "s"}`;
      }
      return result;
    }
    return `${isNegative}${hours} hour${hours === 1 ? "" : "s"}`;
  },
  tryConvertStringToInt(string: string) {
    const number = parseInt(string, 10);
    if (Number.isNaN(number)) return string;
    return number;
  },
  ideaMeetsChallengeVisibilityLimits(
    idea: Pick<OpenAPI.Schemas["Idea"], "isPinned" | "isSubmitted"> | undefined,
    challenge:
      | Pick<ChallengeLike, "ideaVisibility" | "ideaVisibilityLimits">
      | undefined,
  ) {
    // A challenge may have visibility limits when it allows users to view ideas
    // For example, this is an array that could be ['pinned', 'submitted']
    // In this instance, a user can only view ideas that are pinned or submitted
    // If there are no visibility limits, then the user can view all ideas, as long as the challenge has an open idea list

    if (!idea || !challenge) {
      return false;
    }

    const { ideaVisibilityLimits = [] } = challenge;
    if (challenge.ideaVisibility !== "users") {
      return false;
    }
    if (
      challenge.ideaVisibility === "users" &&
      ideaVisibilityLimits.length === 0
    ) {
      return true;
    }
    if (ideaVisibilityLimits.includes("pinned") && idea.isPinned) {
      return true;
    }
    if (ideaVisibilityLimits.includes("submitted") && idea.isSubmitted) {
      return true;
    }
    return false;
  },
  variablesAreEqual(var1: any, var2: any) {
    if (typeof var1 !== typeof var2) {
      return false;
    }

    if (var1 === null || var2 === null) {
      return var1 === var2;
    }

    if (Array.isArray(var1)) {
      if (!Array.isArray(var2)) {
        return false;
      }

      if (var1.length !== var2.length) {
        return false;
      }

      for (let i = 0; i < var1.length; i += 1) {
        if (!util.variablesAreEqual(var1[i], var2[i])) {
          return false;
        }
      }

      return true;
    }

    if (typeof var1 === "object") {
      const var1Keys = Object.keys(var1);
      const var2Keys = Object.keys(var2);

      if (var1Keys.length !== var2Keys.length) {
        return false;
      }

      for (const key of var1Keys) {
        if (!util.variablesAreEqual(var1[key], var2[key])) {
          return false;
        }
      }

      return true;
    }

    return var1 === var2;
  },
  /** Resolves with the URL of the generated PDF. */
  downloadCompanyPdf(company: OpenAPI.Schemas["InnointelCompanies"]) {
    return new Promise<string>((resolve, reject) => {
      api.auth.requestWebdriverToken(
        `/companies?company=${company._id}&innointel=true`,
        ({ token }) => {
          const xhr = new XMLHttpRequest();
          xhr.open(
            "GET",
            `${import.meta.env.VITE_WEBDRIVER}/companies?company=${company._id}&innointel=true`,
          );
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                if (data?.url) {
                  resolve(data.url as string);
                } else {
                  reject(new Error(i18n.t("ideas.pdf.error")));
                }
              } else {
                reject(new Error(i18n.t("ideas.pdf.error")));
              }
            }
          };
          xhr.send();
        },
        () => {
          reject(new Error("Could not generate PDF"));
        },
      );
    });
  },
  canAccessSuperadminOrgList(
    user: Pick<OpenAPI.Schemas["User"], "permissions"> | undefined,
  ) {
    if (!user) {
      return false;
    }
    const userPermissions = user.permissions ?? {};
    for (const [scopeKey, permissions] of Object.entries(userPermissions)) {
      if (
        scopeKey.includes("global") &&
        permissions.includes("super.listOrgs")
      ) {
        return true;
      }
    }
    return false;
  },
  isAssessableField(
    field: NonNullable<
      NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]["fields"]
    >[number],
  ) {
    return (
      field.type === "assessment" ||
      field.type === "dropdown" ||
      field.type === "assessmentFeedback"
    );
  },
  assessmentFieldIsComplete(
    assessment:
      | NonNullable<
          OpenAPI.Schemas["Idea"]["assessments"]
        >[number]["assessment"]
      | undefined,
    field: NonNullable<
      NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]["fields"]
    >[number],
  ) {
    const value = assessment?.[field.id]?.value;

    if (field.type === "infotext") {
      return true;
    } else if (field.type === "assessmentFeedback") {
      return value !== undefined && value !== "" && value !== null;
    } else if (field.type === "assessment") {
      const score = assessment?.[field.id]?.score;
      if (score === "") {
        return false;
      }
      return score !== undefined && score !== null;
    }
    return value !== undefined && value !== "" && value !== null;
  },
  assessmentIsSubmissable(
    assessment:
      | NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number]
      | undefined,
    ideaTemplate: OpenAPI.Schemas["IdeaTemplate"] | undefined,
  ) {
    if (!assessment || !ideaTemplate) {
      return false;
    }
    if (assessment.isSubmitted) {
      return false;
    }

    const assessmentRoundId = assessment.assessmentRoundId;
    const assessmentRound = ideaTemplate.assessments?.rounds?.find(
      (ar) => ar.id === assessmentRoundId,
    );

    const assessmentClosed = assessmentRound?.assessmentClosed || false;

    if (assessmentClosed) {
      return false;
    }

    const assessmentFields = ideaTemplate.body?.flatMap((section) => {
      if (section.type !== "assessment" || !section.fields) {
        return [];
      }
      if (section.assessmentRoundId !== assessment.assessmentRoundId) {
        return [];
      }
      return section.fields;
    });
    const assessableFields = assessmentFields?.filter((field) =>
      util.isAssessableField(field),
    );
    const allFieldsComplete = assessableFields?.every((field) =>
      util.assessmentFieldIsComplete(assessment.assessment, field),
    );

    const hasConflictNotes = !!assessment.conflictOfInterest?.notes;

    return (
      (assessment.conflictOfInterest?.hasConflictOfInterest === false &&
        allFieldsComplete) ||
      (assessment.conflictOfInterest?.hasConflictOfInterest === true &&
        hasConflictNotes)
    );
  },
  onUpdateIdea(
    idea:
      | (Pick<CachedIdea, "_id"> & { challenge?: CachedIdea["challenge"] })
      | undefined,
    data: Partial<CachedIdea> | unknown,
    fieldPath?: string[],
  ) {
    // Guard against null/undefined idea. Some callers invoke this from
    // deferred contexts (e.g. setTimeout) where the captured idea may have
    // been cleared by the time it runs. Without this guard, dereferencing
    // `idea._id` / `idea.challenge` throws a TypeError.
    if (!idea) {
      return;
    }
    util.updateCachedIdea(idea, (base) => {
      const updatedIdea = Object.assign({}, base) as CachedIdea;
      if (fieldPath) {
        const clone = structuredClone(updatedIdea);
        set(clone, fieldPath, data);
        return clone;
      }
      return Object.assign(updatedIdea, data as object) as CachedIdea;
    });
  },
  /**
   * Updates the cached idea from its latest cached state, so a save that
   * finishes late doesn't undo changes made since it started.
   */
  updateCachedIdea(
    idea: Pick<CachedIdea, "_id"> & { challenge?: CachedIdea["challenge"] },
    update: (prevIdea: CachedIdea) => CachedIdea,
  ) {
    const updater = (prevIdea: CachedIdea | undefined): CachedIdea =>
      update((prevIdea ?? idea) as CachedIdea);
    api.setQueryData(api.ideas.get, [idea._id], updater);
    if (idea.challenge) {
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...(prev || {}),
        ideas:
          prev?.ideas?.map((i) =>
            i._id === idea._id
              ? (updater(i as CachedIdea) as (typeof prev.ideas)[number])
              : i,
          ) ?? [],
      }));
    }
  },
};

isLocalStorageSupported = util.localStorageIsSupported();

export default util;
